import { t } from '../i18n';

function resolveApiBaseUrl(): string {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  return `${window.location.origin}/api`;
}

const API_BASE_URL = resolveApiBaseUrl();

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  already_exists?: boolean;
}

async function getAuthToken(): Promise<string> {
  if (!window.shopify?.idToken) {
    throw new Error(t('api_bridge_not_init'));
  }

  return window.shopify.idToken();
}

export class ApiError extends Error {
  code?: string;
}

async function fetchWithAuthResponse<T>(
  endpoint: string,
  options: RequestInit = {},
  retried = false,
): Promise<ApiResponse<T>> {
  let token: string;
  try {
    token = await getAuthToken();
  } catch {
    throw new Error(t('api_token_error'));
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...(options.headers as Record<string, string> || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401 && !retried) {
    try {
      return await fetchWithAuthResponse<T>(endpoint, options, true);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new Error(t('session_expired'));
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const details = body.errors ? '\n' + body.errors.join('\n') : '';
    const err = new ApiError((body.message || `${t('api_error')} ${response.status}`) + details);
    err.code = body.error_code;
    throw err;
  }

  return response.json();
}

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const response = await fetchWithAuthResponse<T>(endpoint, options);
  return response.data;
}

export async function apiGet<T>(endpoint: string): Promise<T> {
  return fetchWithAuth<T>(endpoint, { method: 'GET' });
}

export async function apiPost<T>(endpoint: string, body?: unknown): Promise<T> {
  return fetchWithAuth<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiPostFull<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
  return fetchWithAuthResponse<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiPut<T>(endpoint: string, body?: unknown): Promise<T> {
  return fetchWithAuth<T>(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiDownload(endpoint: string, filename: string): Promise<void> {
  let token: string;
  try {
    token = await getAuthToken();
  } catch {
    throw new Error(t('api_token_error'));
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`${t('download_failed')} ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

export async function getPlanInfo(): Promise<import('../types').PlanInfo> {
  return apiGet<import('../types').PlanInfo>('/billing/plan');
}

export async function confirmBillingPlan(
  planHandle: string,
  chargeId?: string | null,
): Promise<import('../types').PlanInfo> {
  return apiPost<import('../types').PlanInfo>('/billing/confirm', {
    plan_handle: planHandle,
    charge_id: chargeId || undefined,
  });
}

export async function getAvailablePlans(): Promise<import('../types').AvailablePlan[]> {
  return apiGet<import('../types').AvailablePlan[]>('/billing/plans');
}
